import { useState, useMemo } from "react";

export interface SearchResult {
  title: string;
  description: string;
  path: string;
  section: string;
  category: "producer" | "crew" | "quick-reference" | "troubleshooting";
}

// All searchable content from Field Guide pages
const searchableContent: SearchResult[] = [
  // Producer Guide - Getting Started
  {
    title: "Overview",
    description:
      "Learn the basics of PROduce and how to set up your first production. Production management platform for live events and festivals.",
    path: "/field-guide/producer#getting-started-overview",
    section: "Getting Started",
    category: "producer",
  },
  {
    title: "Create an Account",
    description: "Navigate to producer login, sign up, enter details, verify email.",
    path: "/field-guide/producer#getting-started-create-account",
    section: "Getting Started",
    category: "producer",
  },
  {
    title: "Dashboard Overview",
    description:
      "Your central hub for managing productions. Productions list, create new, settings.",
    path: "/field-guide/producer#getting-started-dashboard",
    section: "Getting Started",
    category: "producer",
  },

  // Producer Guide - Production Setup
  {
    title: "Basic Information",
    description: "Festival name, client name, location, event dates, opening hours.",
    path: "/field-guide/producer#production-setup-basic-info",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Stages per Day",
    description: "Add stages, configure per day, copy to other days.",
    path: "/field-guide/producer#production-setup-stages",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Timetable Import",
    description: "Upload PDF, AI extraction, review and correct, manual editing.",
    path: "/field-guide/producer#production-setup-timetable",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "The Crew",
    description: "Add crew member, assign color, multi-day setup, existing users.",
    path: "/field-guide/producer#production-setup-crew",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Festival Map",
    description: "Center the map, draw stage zones, mark special locations, geofencing.",
    path: "/field-guide/producer#production-setup-map",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Crew Schedules",
    description:
      "Select crew member, add assignments, artist assignments, drag and resize, conflict detection.",
    path: "/field-guide/producer#production-setup-schedules",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "General Briefing",
    description:
      "Catering, dress code, check-in procedure, equipment, restrictions, general instructions.",
    path: "/field-guide/producer#production-setup-briefing",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Live Tracking Setup",
    description: "GPS interval, tracking behavior, privacy considerations.",
    path: "/field-guide/producer#production-setup-tracking-setup",
    section: "Production Setup",
    category: "producer",
  },
  {
    title: "Review and Publish",
    description: "Review summary, preview, publish production, send invitations.",
    path: "/field-guide/producer#production-setup-review-publish",
    section: "Production Setup",
    category: "producer",
  },

  // Producer Guide - Management
  {
    title: "Production Dashboard",
    description: "Quick stats, crew overview, recent activity.",
    path: "/field-guide/producer#production-management-production-dashboard",
    section: "Production Management",
    category: "producer",
  },
  {
    title: "PDF Exports",
    description: "Export schedules, briefings, and analytics to PDF.",
    path: "/field-guide/producer#production-management-pdf-exports",
    section: "Production Management",
    category: "producer",
  },
  {
    title: "Inviting Crew Members",
    description: "Add crew, send invitations, email delivery.",
    path: "/field-guide/producer#crew-management-invite-crew",
    section: "Crew Management",
    category: "producer",
  },
  {
    title: "Delivery Status",
    description: "Pending, sent, delivered, opened, failed status indicators.",
    path: "/field-guide/producer#crew-management-delivery-status",
    section: "Crew Management",
    category: "producer",
  },
  {
    title: "Re-sending Invitations",
    description: "Resend invitations to crew members.",
    path: "/field-guide/producer#crew-management-re-invites",
    section: "Crew Management",
    category: "producer",
  },
  {
    title: "Timeline Navigation",
    description: "Scroll, zoom, and navigate the schedule timeline.",
    path: "/field-guide/producer#schedule-view-timeline-nav",
    section: "Schedule View",
    category: "producer",
  },
  {
    title: "Day Switching",
    description: "Switch between days in multi-day events.",
    path: "/field-guide/producer#schedule-view-day-switching",
    section: "Schedule View",
    category: "producer",
  },
  {
    title: "Export Options",
    description: "Export schedules in various formats.",
    path: "/field-guide/producer#schedule-view-export-options",
    section: "Schedule View",
    category: "producer",
  },
  {
    title: "Map Interface",
    description: "Live tracking map with crew locations and stage zones.",
    path: "/field-guide/producer#live-tracking-map-interface",
    section: "Live Tracking",
    category: "producer",
  },
  {
    title: "Crew Locations",
    description: "See where crew members are in real-time.",
    path: "/field-guide/producer#live-tracking-crew-locations",
    section: "Live Tracking",
    category: "producer",
  },
  {
    title: "Stage Zones",
    description: "Geofenced areas for tracking crew presence.",
    path: "/field-guide/producer#live-tracking-stage-zones",
    section: "Live Tracking",
    category: "producer",
  },
  {
    title: "Analytics Dashboard",
    description: "Overview of crew performance and coverage.",
    path: "/field-guide/producer#analytics-overview-analytics",
    section: "Analytics",
    category: "producer",
  },
  {
    title: "Profile Settings",
    description: "Update your account information.",
    path: "/field-guide/producer#settings-profile",
    section: "Settings",
    category: "producer",
  },
  {
    title: "Notifications",
    description: "Configure notification preferences.",
    path: "/field-guide/producer#settings-notifications",
    section: "Settings",
    category: "producer",
  },
  {
    title: "Emergency Mode",
    description: "Special mode for urgent situations during events.",
    path: "/field-guide/producer#settings-emergency-mode",
    section: "Settings",
    category: "producer",
  },

  // Crew Guide - Getting Started
  {
    title: "Receiving Your Invitation",
    description: "Check email, click access link, spam folder.",
    path: "/field-guide/crew#getting-started-invitation",
    section: "Getting Started",
    category: "crew",
  },
  {
    title: "Creating Your Account",
    description: "Click invitation link, create password, verify email.",
    path: "/field-guide/crew#getting-started-account",
    section: "Getting Started",
    category: "crew",
  },
  {
    title: "Installing the App",
    description: "iPhone Safari, Android Chrome, add to home screen, PWA installation.",
    path: "/field-guide/crew#getting-started-install",
    section: "Getting Started",
    category: "crew",
  },
  {
    title: "First Login",
    description: "Enable notifications, enable location, explore the app.",
    path: "/field-guide/crew#getting-started-first-login",
    section: "Getting Started",
    category: "crew",
  },

  // Crew Guide - Home Screen
  {
    title: "Home Screen Overview",
    description: "Production name, next assignment, updates, your info.",
    path: "/field-guide/crew#home-screen-overview",
    section: "Home Screen",
    category: "crew",
  },
  {
    title: "Production Information",
    description: "Event dates, venue, doors open/close times.",
    path: "/field-guide/crew#home-screen-production-info",
    section: "Home Screen",
    category: "crew",
  },
  {
    title: "Timeline Updates",
    description: "Recent changes and announcements.",
    path: "/field-guide/crew#home-screen-updates",
    section: "Home Screen",
    category: "crew",
  },
  {
    title: "Crew List",
    description: "View other crew members and their schedules.",
    path: "/field-guide/crew#home-screen-crew-list",
    section: "Home Screen",
    category: "crew",
  },

  // Crew Guide - Schedule
  {
    title: "Viewing Your Schedule",
    description: "Navigate to schedule, select day, timeline view.",
    path: "/field-guide/crew#your-schedule-viewing",
    section: "Your Schedule",
    category: "crew",
  },
  {
    title: "Understanding Assignments",
    description: "Time, artist, stage, notes, upcoming, in progress, completed.",
    path: "/field-guide/crew#your-schedule-assignments",
    section: "Your Schedule",
    category: "crew",
  },
  {
    title: "Task Details",
    description: "Tap assignment for full details, navigate to stage.",
    path: "/field-guide/crew#your-schedule-task-details",
    section: "Your Schedule",
    category: "crew",
  },

  // Crew Guide - Timetable
  {
    title: "Full Lineup",
    description: "View all artists and performances across stages.",
    path: "/field-guide/crew#timetable-full-lineup",
    section: "Timetable",
    category: "crew",
  },
  {
    title: "Stage Filtering",
    description: "Filter timetable to show specific stages.",
    path: "/field-guide/crew#timetable-stage-filter",
    section: "Timetable",
    category: "crew",
  },
  {
    title: "Artist Information",
    description: "Tap artist to see set details.",
    path: "/field-guide/crew#timetable-artist-info",
    section: "Timetable",
    category: "crew",
  },

  // Crew Guide - Briefing
  {
    title: "Event Details",
    description: "All essential production information.",
    path: "/field-guide/crew#briefing-event-details",
    section: "Briefing",
    category: "crew",
  },
  {
    title: "Catering & Facilities",
    description: "Meal times, locations, facilities information.",
    path: "/field-guide/crew#briefing-catering",
    section: "Briefing",
    category: "crew",
  },
  {
    title: "Equipment Settings",
    description: "Camera and audio settings for the production.",
    path: "/field-guide/crew#briefing-equipment",
    section: "Briefing",
    category: "crew",
  },
  {
    title: "Festival Map",
    description: "Interactive map with stage locations.",
    path: "/field-guide/crew#briefing-festival-map",
    section: "Briefing",
    category: "crew",
  },

  // Crew Guide - Live Tracking
  {
    title: "Map View",
    description: "View the festival map with crew locations.",
    path: "/field-guide/crew#live-tracking-map-view",
    section: "Live Tracking",
    category: "crew",
  },
  {
    title: "Your Location",
    description: "See your own location on the map.",
    path: "/field-guide/crew#live-tracking-your-location",
    section: "Live Tracking",
    category: "crew",
  },
  {
    title: "Finding Other Crew",
    description: "Locate other crew members on the map.",
    path: "/field-guide/crew#live-tracking-other-crew",
    section: "Live Tracking",
    category: "crew",
  },

  // Crew Guide - Settings
  {
    title: "Crew Notifications",
    description: "Configure push notification preferences.",
    path: "/field-guide/crew#settings-notifications",
    section: "Settings",
    category: "crew",
  },
  {
    title: "Location Settings",
    description: "GPS and location sharing preferences.",
    path: "/field-guide/crew#settings-location",
    section: "Settings",
    category: "crew",
  },
  {
    title: "Offline Data",
    description: "Manage cached data for offline use.",
    path: "/field-guide/crew#settings-offline",
    section: "Settings",
    category: "crew",
  },

  // Crew Guide - Offline Mode
  {
    title: "What Works Offline",
    description: "Features available without internet connection.",
    path: "/field-guide/crew#offline-mode-what-works",
    section: "Working Offline",
    category: "crew",
  },
  {
    title: "Data Synchronization",
    description: "How data syncs when connection returns.",
    path: "/field-guide/crew#offline-mode-sync",
    section: "Working Offline",
    category: "crew",
  },

  // Quick Reference
  {
    title: "Invitation Status Indicators",
    description: "Pending, sent, delivered, opened, failed status meanings.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "Assignment Status",
    description: "Upcoming, starting soon, in progress, completed.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "Common Crew Functions",
    description:
      "Camera operator, photographer, stage manager, runner, interviewer, social media, audio tech.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "Map Zone Types",
    description: "Stage zone, festival area, special location.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "Notification Types",
    description:
      "Assignment reminder, new assignment, updated, deleted, announcement, emergency alert.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "Time Format Reference",
    description: "24-hour, 12-hour, after midnight formats.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },
  {
    title: "GPS Accuracy Indicators",
    description: "Excellent, good, fair, poor accuracy and causes.",
    path: "/field-guide/quick-reference",
    section: "Quick Reference",
    category: "quick-reference",
  },

  // Troubleshooting
  {
    title: "I didn't receive my invitation email",
    description: "Check spam, verify email address, ask producer to resend.",
    path: "/field-guide/troubleshooting",
    section: "Login & Account Issues",
    category: "troubleshooting",
  },
  {
    title: "I forgot my password",
    description: "Use forgot password link, check email for reset link.",
    path: "/field-guide/troubleshooting",
    section: "Login & Account Issues",
    category: "troubleshooting",
  },
  {
    title: "I can't verify my email",
    description: "Check if link expired, request new verification.",
    path: "/field-guide/troubleshooting",
    section: "Login & Account Issues",
    category: "troubleshooting",
  },
  {
    title: "I'm not receiving push notifications",
    description: "Check iPhone/Android settings, install app, enable notifications.",
    path: "/field-guide/troubleshooting",
    section: "Notification Issues",
    category: "troubleshooting",
  },
  {
    title: "Notifications arrive late",
    description: "Battery saver, do not disturb, background activity.",
    path: "/field-guide/troubleshooting",
    section: "Notification Issues",
    category: "troubleshooting",
  },
  {
    title: "My location isn't showing",
    description: "Check location services, permissions, GPS settings.",
    path: "/field-guide/troubleshooting",
    section: "GPS & Location Issues",
    category: "troubleshooting",
  },
  {
    title: "My location is inaccurate",
    description: "Indoor locations, buildings, high accuracy mode.",
    path: "/field-guide/troubleshooting",
    section: "GPS & Location Issues",
    category: "troubleshooting",
  },
  {
    title: "Location tracking drains battery",
    description: "Reduce tracking frequency, track only during assignments.",
    path: "/field-guide/troubleshooting",
    section: "GPS & Location Issues",
    category: "troubleshooting",
  },
  {
    title: "My schedule isn't showing assignments",
    description: "Check day tabs, producer hasn't created assignments.",
    path: "/field-guide/troubleshooting",
    section: "Schedule Issues",
    category: "troubleshooting",
  },
  {
    title: "The app isn't working offline",
    description: "Open app while online first, cache data.",
    path: "/field-guide/troubleshooting",
    section: "Offline & Sync Issues",
    category: "troubleshooting",
  },
  {
    title: "PDF export issues",
    description: "Export taking long, missing content, styling.",
    path: "/field-guide/troubleshooting",
    section: "PDF Export Issues",
    category: "troubleshooting",
  },
];

export function useFieldGuideSearch() {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const results = useMemo(() => {
    if (!query.trim() || query.length < 2) return [];

    const searchTerms = query.toLowerCase().split(" ").filter(Boolean);

    return searchableContent
      .filter((item) => {
        const searchText = `${item.title} ${item.description} ${item.section}`.toLowerCase();
        return searchTerms.every((term) => searchText.includes(term));
      })
      .slice(0, 8); // Limit to 8 results
  }, [query]);

  const categoryLabels: Record<SearchResult["category"], string> = {
    producer: "Producer Guide",
    crew: "Crew Guide",
    "quick-reference": "Quick Reference",
    troubleshooting: "Troubleshooting",
  };

  return {
    query,
    setQuery,
    results,
    isOpen,
    setIsOpen,
    categoryLabels,
  };
}
